import {
    useState, useEffect, useCallback, useRef, useMemo,
} from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import axiosManagerRegister from "../../../services/axiosManagerRegister";
import { formatTo } from "../../../utils/dateTimeHelper";

export const STEPS = {
    DETAILS: "DETAILS",
    TIMEZONE: "TIMEZONE",
    YEAR_GROUP: "YEAR_GROUP",
    SUBJECTS: "SUBJECTS",
    HIGHER_SUBJECTS: "HIGHER_SUBJECTS",
    CONFIRMATION: "CONFIRMATION",
};

const STORAGE_KEY = "addStudent_tempData";
const STEP_PARAM = "step";
const STEP_EDIT_MODE = "edit";
const COMPLETE_STEP = "complete";

const safeJSONParse = (str, fallback = {}) => {
    try {
        return JSON.parse(str) || fallback;
    } catch {
        return fallback;
    }
};

const safeLocalStorage = {
    getItem: (key) => {
        try {
            return localStorage.getItem(key);
        } catch {
            return null;
        }
    },
    setItem: (key, value) => {
        try {
            localStorage.setItem(key, value);
        } catch {
            // Silently fail
        }
    },
    removeItem: (key) => {
        try {
            localStorage.removeItem(key);
        } catch {
            // Silently fail
        }
    },
};

const persistentDataDefaults = {
    registrationId: null,
    sessionKey: null,
};

const formDataDefaults = {
    detailsData: {},
    divisionSelected: null,
    yearGroupSelected: null,
    packageSelected: null,
    subjectsSelected: [],
};

export const useAddStudent = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const step = searchParams.get(STEP_PARAM);
    const isEditMode = searchParams.has(STEP_EDIT_MODE);
    const isComplete = searchParams.has(COMPLETE_STEP);

    useEffect(() => {
        if (!step && !isComplete) {
            setSearchParams((prev) => {
                const newParams = new URLSearchParams(prev);
                newParams.set(STEP_PARAM, STEPS.DETAILS);
                return newParams;
            }, { replace: true });
        }
    }, [step, setSearchParams, isComplete]);

    const [persistentData, setPersistentData] = useState(() => {
        const saved = safeLocalStorage.getItem(STORAGE_KEY);
        return saved ? safeJSONParse(saved, persistentDataDefaults) : persistentDataDefaults;
    });

    const [wasLoadedFromStorage] = useState(() => {
        const saved = safeLocalStorage.getItem(STORAGE_KEY);
        const savedData = saved ? safeJSONParse(saved, {}) : {};
        return !!(savedData.registrationId && savedData.sessionKey);
    });

    const [formData, setFormData] = useState(formDataDefaults);

    const registrationId = persistentData?.registrationId;
    const childId = formData?.detailsData?.childId;
    const yearGroupSelected = formData?.yearGroupSelected;

    const [isSessionSet, setIsSessionSet] = useState(false);
    const [isClearing, setIsClearing] = useState(false);

    useEffect(() => {
        if (!isClearing) {
            safeLocalStorage.setItem(STORAGE_KEY, JSON.stringify(persistentData));
        }
    }, [persistentData, isClearing]);

    useEffect(() => {
        if (persistentData?.sessionKey && !isSessionSet) {
            axiosManagerRegister.setSessionKey(persistentData.sessionKey);
            setIsSessionSet(true);
        }
    }, [persistentData.sessionKey, isSessionSet]);

    const setStep = useCallback((newStep, isEditMode) => {
        setSearchParams((prev) => {
            const newParams = new URLSearchParams(prev);

            newParams.set(STEP_PARAM, newStep);
            if (isEditMode) newParams.set(STEP_EDIT_MODE, true);
            return newParams;
        }, { replace: true });
    }, [setSearchParams]);

    const updatePersistentData = useCallback((updates) => {
        setPersistentData((prev) => ({ ...prev, ...updates }));
    }, []);

    const updateFormData = useCallback((updates) => {
        setFormData((prev) => ({ ...prev, ...updates }));
    }, []);

    const clearStoredData = useCallback((skipNavigation = false) => {
        setIsClearing(true);

        safeLocalStorage.removeItem(STORAGE_KEY);
        axiosManagerRegister.removeSessionKey();

        setPersistentData(persistentDataDefaults);
        setFormData(formDataDefaults);
        setIsSessionSet(false);

        if (!skipNavigation) {
            setSearchParams((prev) => {
                const p = new URLSearchParams(prev);
                p.delete(STEP_PARAM);
                return p;
            }, { replace: true });
        }

        setTimeout(() => {
            setIsClearing(false);
        }, 100);
    }, [setSearchParams]);

    const hasStoredData = useMemo(
        () => Boolean(persistentData.registrationId || persistentData.sessionKey),
        [persistentData.registrationId, persistentData.sessionKey],
    );

    const shouldLoadInitialData = wasLoadedFromStorage && isSessionSet && !!registrationId;

    const { data: leadData, isLoading: isLoadingLead } = useQuery({
        queryKey: [`add-student/${registrationId}/lead`],
        queryFn: () => axiosManagerRegister.get$(`/api/api/registration/${registrationId}/lead`).then((response) => response?.data),
        retry: 0,
        enabled: shouldLoadInitialData,
    });
    useEffect(() => {
        if (leadData?.relationshipTypeId) {
            const detailsData = {
                ...formData.detailsData,
                relationshipTypeId: leadData.relationshipTypeId,
            };
            updateFormData({ detailsData });
        }
    }, [leadData?.relationshipTypeId]);

    const { data: studentData, isLoading: isLoadingStudentData } = useQuery({
        queryKey: [`add-student/${registrationId}/children`],
        queryFn: () => axiosManagerRegister.get$(`/api/api/registration/${registrationId}/child`).then((response) => response?.data?.[0]),
        retry: 0,
        enabled: shouldLoadInitialData,
    });
    const detailsData = useMemo(() => (studentData ? ({
        ...formData.detailsData,
        childId: studentData.onlineRegistrationChildId,
        forename: studentData.forename,
        surname: studentData.surname,
        dateOfBirth: studentData.dateOfBirth,
        genderTypeId: studentData.genderTypeId,
        currentNotionalYearGroup: studentData.currentNotionalYearGroup,
        countryOfResidence: studentData.countryOfResidence,
        desiredStartDate: studentData.desiredStartDate ? formatTo(studentData.desiredStartDate, "yyyy-MM-dd") : undefined,
        studySchedule: studentData.studySchedule,
    }) : null), [studentData]);

    useEffect(() => {
        if (detailsData) {
            const yearGroupSelected = studentData.yearGroupId ? { yearGroupId: studentData.yearGroupId } : null;

            updateFormData({ detailsData, divisionSelected: studentData.schoolDivisionId, yearGroupSelected });
        }
    }, [detailsData, studentData?.yearGroupId]);

    const { data: productsData, isLoading: isLoadingProductsData } = useQuery({
        queryKey: [`add-student/${registrationId}/child/${childId}/products`],
        queryFn: () => axiosManagerRegister.get$(`/api/api/registration/${registrationId}/child/${childId}/products`).then((response) => response?.data),
        retry: 0,
        enabled: shouldLoadInitialData && !!childId && !!yearGroupSelected,
    });

    useEffect(() => {
        if (productsData) {
            const packageSelected = productsData.product?.externalProductId ? { productId: productsData.product?.externalProductId } : null;
            const subjectsSelected = (productsData.product?.subjects || []).map((item) => item.externalProductId);

            updateFormData({ packageSelected, subjectsSelected });
        }
    }, [productsData]);

    return {
        step,
        setStep,
        isEditMode,

        isComplete,
        isSessionSet,
        isLoadingData: isLoadingLead || isLoadingStudentData || isLoadingProductsData,

        // Persistent data
        registrationId: persistentData.registrationId,
        sessionKey: persistentData.sessionKey,

        // Form data (user selections during the flow)
        detailsData: formData.detailsData,
        divisionSelected: formData.divisionSelected,
        yearGroupSelected: formData.yearGroupSelected,
        packageSelected: formData.packageSelected,
        subjectsSelected: formData.subjectsSelected,

        // Persistent data setters
        setRegistrationId: useCallback((registrationId) => updatePersistentData({ registrationId }), [updatePersistentData]),
        setSessionKey: useCallback((sessionKey) => updatePersistentData({ sessionKey }), [updatePersistentData]),

        // Form data setters
        setDetailsData: useCallback((detailsData) => updateFormData({ detailsData }), [updateFormData]),
        setDivisionSelected: useCallback((divisionSelected) => updateFormData({ divisionSelected }), [updateFormData]),
        setYearGroupSelected: useCallback((yearGroupSelected) => updateFormData({ yearGroupSelected }), [updateFormData]),
        setPackageSelected: useCallback((packageSelected) => updateFormData({ packageSelected }), [updateFormData]),
        setSubjectsSelected: useCallback((subjectsSelected) => updateFormData({ subjectsSelected }), [updateFormData]),

        // Batch updaters
        updatePersistentData,
        updateFormData,

        // Utils
        clearStoredData,
        hasStoredData,
        isClearing,
    };
};
